<template>
  <div
    v-if="!loading"
    class="is-flex is-flex-direction-column"
    style="gap: 1rem"
  >
    <div class="is-flex mt-2" style="gap: 1rem">
      <b-field label="Client(s)" class="report_config__select mb-0">
        <MultiSelect
          v-model="salesReportParams.clients"
          label="name"
          track-by="id"
          :options="clientOptions"
          :multiple="true"
          :taggable="true"
          tag-position="top"
          :max-height="200"
          :close-on-select="true"
          :clear-on-select="false"
          :preserve-search="true"
          :allow-empty="true"
          placeholder="Select a Client"
          @input="() => handleRequestsOptionsClientsUpdate(salesReportParams)"
        >
        </MultiSelect>
      </b-field>

      <b-field label="User(s)" class="report_config__select mb-0">
        <MultiSelect
          v-model="salesReportParams.users"
          label="name"
          track-by="id"
          :loading="isLoadingUsers"
          :options="userOptions"
          :multiple="true"
          :taggable="true"
          tag-position="top"
          :max-height="200"
          :close-on-select="true"
          :clear-on-select="false"
          :preserve-search="true"
          :allow-empty="true"
          placeholder="Select a User"
          @search-change="validateSearchingUsers"
          @input="() => handleRequestsOptionsUsersUpdate(salesReportParams)"
        >
        </MultiSelect>
      </b-field>
    </div>

    <div class="mb-2">
      <b-field :label="rangeDateLabel" class="range-date is-relative">
        <BaseDatePicker
          v-model="salesReportParams.from"
          placeholder="From"
          :min="salesReportParams.min_from"
          :max="salesReportParams.max_from"
          size="medium"
          class="request_date__field"
          :is-not-valid="false"
        />
        <BaseDatePicker
          v-model="salesReportParams.to"
          placeholder="To"
          :min="salesReportParams.min_from"
          :max="salesReportParams.max_from"
          size="medium"
          class="request_date__field ml-4"
          :is-not-valid="false"
        />
        <p
          style="
            position: absolute;
            text-decoration: underline;
            top: 0;
            right: 0;
            cursor: pointer;
          "
          @click="resetSalesReportFilters"
        >
          <span class="is-flex is-align-items-center" style="font-size: 0.8rem">
            Clear Filters
          </span>
        </p>
      </b-field>
    </div>

    <div class="is-flex mb-5" style="gap: 1rem">
      <b-field label="Service Code" class="report_config__select mb-0">
        <b-input
          v-model="salesReportParams.service"
          placeholder="Service Code (Optional)"
        />
      </b-field>

      <b-field label="Itinerary Code" class="report_config__select mb-0">
        <b-input
          v-model="salesReportParams.itinerary"
          placeholder="Itinerary Code (Optional)"
        />
      </b-field>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { USER_ROLE_IDS, DATE_FORMAT_YYYY_MM_DD } from "@/constants";
import BaseButton from "@/components/base-ui/BaseButton.vue";
import IconSearch from "@/components/icons/IconSearch.vue";
import { format } from "date-fns";
import MultiSelect from "vue-multiselect";

export default {
  name: "ReportFilterForm",
  components: { IconSearch, BaseButton, MultiSelect },
  props: {
    value: Object,
    loadReports: {
      type: Function,
      default: () => {},
    },
    rangeDateLabel: {
      type: String,
      default: "Creation Range Date",
    },
    userPicker: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const min_from = format(new Date(2024, 0, 1), DATE_FORMAT_YYYY_MM_DD);

    const now = new Date();
    now.setDate(now.getDate() - 1);

    const max_from = format(now, DATE_FORMAT_YYYY_MM_DD);

    const clients = [{ id: "", name: "All Clients" }];

    const users = [{ id: "", name: "All Users" }];

    const usersText = "All Users";

    const clientsText = "All Clients";

    return {
      isLoadingUsers: false,
      defaultSalesReportParams: {
        min_from: min_from,
        max_from: max_from,
        from: min_from,
        to: max_from,
        users,
        usersText,
        clients,
        clientsText,
        only_with_request: true,
        itinerary: null,
        service: null,
      },
      loading: true,
      isSourceDataModalOpen: false,
      areReportsloading: false,
    };
  },
  computed: {
    ...mapGetters({
      authData: "auth/getAuthData",
      requestsByTime: "reports/getQuotesByTime",
      requestsByClients: "reports/getQuotesByClients",
      requestsByStage: "reports/getRequestsByStages",
      requestsByDestinations: "reports/getRequestsByDestinations",
      userSummary: "reports/getUserSummary",
      clients: "client/getClients",
      authUser: "auth/getAuthUserData",
      users: "users/getUsers",
    }),
    clientOptions() {
      return [{ id: "", name: "All Clients" }, ...this.clients];
    },
    userOptions() {
      const defaultOptions = [
        { id: "", name: "All Users" },
        this.authData.user,
      ];

      if (!this.userPicker) return defaultOptions;

      const filteredOptions = this.users.data?.filter(
        (user) => user.id !== this.authData.user.id
      );

      return [...defaultOptions, ...filteredOptions];
    },
    salesReportParams: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit("input", newValue);
      },
    },
  },
  mounted() {
    this.loadInitialData();
  },
  methods: {
    resetSalesReportFilters() {
      this.salesReportParams = {
        ...this.salesReportParams,
        from: this.salesReportParams.min_from,
        to: this.salesReportParams.max_from,
        clients: [{ id: "", name: "All Clients" }],
      };
    },
    handleRequestsOptionsClientsUpdate(params) {
      let clientsText = "All Clients";

      if (params.clients.length > 0) {
        clientsText = params.clients.map((client) => client.name).join(",");
      }

      params.clientsText = clientsText;
    },
    async loadUsers(payload = {}) {
      return await this.$store.dispatch("utils/validateFunctionExecution", {
        functionToExecute: async () => {
          this.isLoadingUsers = true;

          await this.$store.dispatch("users/getUsers", {
            page: 1,
            name: payload?.name || "",
            role: [USER_ROLE_IDS.ADMIN, USER_ROLE_IDS.TRAVEL_AGENT].join(","),
          });

          this.isLoadingUsers = false;
        },
        functionToExecuteOnFail: async (error) => {
          this.loading = false;
          this.isLoadingUsers = false;
          this.$buefy.toast.open({
            duration: 5000,
            message: "Error trying to get users, please try again.",
            position: "is-top",
            type: "is-danger",
          });
          throw error;
        },
      });
    },
    async validateSearchingUsers(event) {
      if (event.length > 3) {
        await this.loadUsers({ name: event });
      }
    },
    handleRequestsOptionsUsersUpdate(params) {
      let usersText = "All Users";

      if (params.users.length > 0) {
        usersText = params.users.map((user) => user.name).join(",");
      }

      params.usersText = usersText;
    },
    async loadClients() {
      return await this.$store.dispatch("utils/validateFunctionExecution", {
        functionToExecute: async () => {
          return await this.$store.dispatch("client/getClients", {
            page: 1,
          });
        },
        functionToExecuteOnFail: async (error) => {
          this.loading = false;
          this.$buefy.toast.open({
            duration: 5000,
            message: "Error trying to get clients, please try again.",
            position: "is-top",
            type: "is-danger",
          });
          throw error;
        },
      });
    },
    closeModal() {
      this.isSourceDataModalOpen = false;
    },
    async loadInitialData() {
      this.loading = true;
      await Promise.allSettled([this.loadClients(), this.loadUsers()]);
      this.salesReportParams = this.defaultSalesReportParams;
      this.loading = false;
    },
  },
};
</script>
